import { useState, useEffect, useMemo, useRef } from 'react';
import { observer, inject } from 'mobx-react';
import { fetch } from '../../utils/api-wrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPencilAlt, faWindowClose, faArrowDown, faArrowUp, faSync } from '@fortawesome/free-solid-svg-icons';
import FilterInputField from '../../components/filter-input-field';
import PaginationControls from '../../components/pagination-controls';
import Select from 'react-select';
import Utils from '../../utils/utils';
import CcardForm from '../../components/ccard-form';
import Modal from '../../components/modal';
import Checkbox from '../../components/checkbox';
import axios from 'axios';
import Switch from 'react-switch';
import toast from 'react-hot-toast';
import PlaidLink from '../../components/plaid-link/plaid-link';
import DropdownMenu from '../../components/dropdown-menu/dropdown-menu';
import { useStatementData, useAutoRefresh } from '../credit-card-statements/hooks';
import ImportTransactionsModal from '../../components/import-transactions-modal/import-transactions-modal';

const AdminCcards = ({ data }) => {
  const cancelToken = useMemo(() => axios.CancelToken.source(), []);
  const [loading, setLoading] = useState(true);
  const [filterValue, setFilterValue] = useState('');
  const [sorting, setSorting] = useState([{ id: 'card_masked_number', desc: false }]);
  const [ccards, setCcards] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [client, setClient] = useState();
  const [clients, setClients] = useState([]);
  const [ccard, setCcard] = useState({}); 
  const [plaidData, setPlaidData] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPlaidPickerModal, setShowPlaidPickerModal] = useState(false);
  const [plaidPickerAccounts, setPlaidPickerAccounts] = useState([]);
  const [plaidPickerLoading, setPlaidPickerLoading] = useState(false);
  const [plaidPickerSelected, setPlaidPickerSelected] = useState(new Set());
  const [plaidPickerSubmitting, setPlaidPickerSubmitting] = useState(false);

  const { refreshing, refreshData } = useAutoRefresh();
  const { generateStatements } = useStatementData({ t: data.t, toast: toast });

  const openEditModal = (ccard) => {
    setCcard(ccard);
    setShowEditModal(true);
  };

  useEffect(() => {
    return () => {
      cancelToken.cancel();
    };
  }, []);

  const updateCcard = (cardOwners, errorCallback = () => {}, callback = () => {}) => {
    fetch(
      `/c_card/${ccard._id.$oid}`,
      'PUT',
      {ccard: ccard, ccard_owners: [...cardOwners?.map((o) => o.value)] ?? []},
      () => {
        setShowEditModal(false);
        setCcard(null);
        refreshCcards();
      },
      () => {
        errorCallback();
        toast.error(data.t.read('error_during_save'));
      },
      false,
      false,
      cancelToken.token
    );
  };

  const activeCcard = (ccard) => {
    fetch(
      `/c_card/${ccard._id.$oid}`,
      'PUT',
      {ccard: ccard},
      () => {
        refreshCcards();
      },
      () => {
        toast.error(data.t.read('error_during_save'));
      },
      false,
      false,
      cancelToken.token
    );
  };

  const createCcard = (cardOwners, errorCallback = () => {}, callback = () => {}) => {
    fetch(
      `/c_card/`,
      'POST',
      {ccard: ccard, ccard_owners: [...cardOwners?.map((o) => o.value)] ?? []},
      () => {
        setShowEditModal(false);
        setCcard(null);
        refreshCcards();
      },
      () => {
        errorCallback();
        toast.error(data.t.read('error_during_save'));
      },
      false,
      false,
      cancelToken.token
    );
  };

  const openPlaidPicker = () => {
    setPlaidPickerLoading(true);
    setShowPlaidPickerModal(true);
    fetch(
      '/c_card/plaid_accounts',
      'GET',
      {},
      (response) => {
        const accounts = response?.data?.accounts ?? [];
        setPlaidPickerAccounts(accounts);
        setPlaidPickerSelected(new Set(accounts.filter((a) => !a.already_imported).map((a) => a.mask)));
        setPlaidPickerLoading(false);
      },
      () => {
        setPlaidPickerLoading(false);
        setShowPlaidPickerModal(false);
        toast.error(data.t.read('error_during_save'));
      },
      false,
      false,
      cancelToken.token
    );
  };

  const togglePlaidPickerMask = (mask) => {
    setPlaidPickerSelected((prev) => {
      const next = new Set(prev);
      if (next.has(mask)) next.delete(mask); else next.add(mask);
      return next;
    });
  };

  const togglePlaidPickerSelectAll = () => {
    const selectable = plaidPickerAccounts.filter((a) => !a.already_imported);
    const allSelected = selectable.length > 0 && selectable.every((a) => plaidPickerSelected.has(a.mask));
    setPlaidPickerSelected(allSelected ? new Set() : new Set(selectable.map((a) => a.mask)));
  };

  const submitPlaidPicker = () => {
    if (plaidPickerSubmitting) return;
    const selections = plaidPickerAccounts
      .filter((a) => plaidPickerSelected.has(a.mask) && !a.already_imported)
      .map((a) => ({ mask: a.mask, label: a.official_name || a.name || `Card ${a.mask}` }));
    if (selections.length === 0) return;
    setPlaidPickerSubmitting(true);
    fetch(
      '/c_card/import_from_plaid',
      'POST',
      { selections },
      (response) => {
        const created = response?.data?.created?.length ?? 0;
        const skipped = response?.data?.skipped_existing?.length ?? 0;
        toast.success(`${created} ${data.t.read('cards_plaid_import_created')}, ${skipped} ${data.t.read('cards_plaid_import_skipped')}`);
        setPlaidPickerSubmitting(false);
        setShowPlaidPickerModal(false);
        refreshCcards();
      },
      () => {
        setPlaidPickerSubmitting(false);
        toast.error(data.t.read('error_during_save'));
      },
      false,
      false,
      cancelToken.token
    );
  };

  //pagination logic
  const indexOfFirstItem = (page - 1) * pageSize;
  const indexOfLastItem = indexOfFirstItem + ccards.length - 1;
  const totalPages = Math.ceil(totalRowCount / pageSize);

  const sortTable = (name) => {
    const sorter = sorting.find((s) => s.id === name);
    if (sorter === undefined) return setSorting([{ id: name, desc: true }]);

    if (sorter.desc) return setSorting([{ id: name, desc: false }]);
    else return setSorting([]);
  };

  const toggleSelect = (id) => setSelectedCardIds((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const selectAllCcards = () => setSelectedCardIds(selectedCardIds.length === ccards?.length ? [] : ccards?.map((m) => m._id?.$oid));
  const isAllSelected = () => ccards?.length && selectedCardIds.length === ccards?.length;
  useEffect(() => {
    refreshClients();
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  const handleRowsPerPageChange = (rows) => {
    setPage(1);
    setPageSize(rows);
  };

  useEffect(() => {
    if (!client) {
      return;
    }
    if (page === 1) {
      refreshCcards();
      return;
    }
    setPage(1);
  }, [pageSize, sorting, filterValue]);

  useEffect(() => {
    if (!client) {
      return;
    }
    refreshCcards();
  }, [page, client]);

  const refreshCcards = () => {
    let start = (page - 1) * pageSize;
    const filters = [];

    if (client?._id) {
      filters.push({ id: 'id_client', value: client._id });
    }

    if (filterValue && filterValue.trim() !== '') {
      filters.push({ id: 'search', value: filterValue.trim() });
    }

    const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(
      JSON.stringify(sorting ?? [])
    )}&start=${encodeURIComponent(start)}&page_size=${pageSize}`;

    fetch(
      `/c_card/${searchData}`,
      'GET',
      {},
      (response) => {
        setCcards(response?.data?.c_cards);
        setTotalRowCount(response?.data?.meta?.totalRowCount);
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
      false,
      false,
      cancelToken.token
    );
  };

  const selectClient = (client) => {
    setClient({ ...client });
    setPage(1);
    setFilterValue('');
  };

  const refreshClients = () => {
    fetch(
      '/clients/',
      'GET',
      {},
      (response) => {
        const clients = response.data.clients;
        setClients(clients);
        const client = clients.find((c) => c._id === data.id_client);
        setClient(client); //Set Client to the user's corresponding client
        setPlaidData(client.plaid ?? []);
        setLoading(false);
      },
      () => {
        // Error handling
        setLoading(false);
      },
      false,
      false,
      cancelToken.token
    );
  };

  const confirmDelete = () => {
    fetch(
      '/c_card/',
      'DELETE',
      { c_card_ids: selectedCardIds },
      () => {
        setSelectedCardIds([]);
        refreshCcards();
        toast.success(data.t.read('Success'));
      },
      () => {
        toast.error(data.t.read('failed_to_delete'));
      }
    );
    setShowDeleteModal(false);
  };

  return (
    <>
      {loading ? (
        <div>
          <div className='d-flex loading-container w-100'>
            <FontAwesomeIcon className='infinite-rotate' icon={faSpinner} />
          </div>
        </div>
      ) : (
        <div>
          <div className='pagination-row'>
            <FilterInputField filterValue={filterValue} setFilterValue={setFilterValue} placeholder={''}></FilterInputField>
            <PaginationControls
              page={page}
              totalPages={totalPages}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              handlePageChange={handlePageChange}
              totalItems={totalRowCount}
              data={data}
              handleRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPage={pageSize}
            ></PaginationControls>
          </div>
          <div className='d-flex justify-content-between align-items-center px-0 py-2'>
            <div className='flex mr-3'>
              <Select
                name='client'
                isDisabled={!Utils.userHasRole(data, ['SuperAdmin'])}
                value={client === null ? null : { value: client, label: client.name }}
                options={clients.map((client) => ({ value: client, label: client.name }))}
                styles={Utils.styleSelect}
                classNamePrefix='select'
                onChange={(choice) => {
                  selectClient(choice.value);
                }}
              />
            </div>
            {
              <div className='d-flex justify-content-end'>
                <PlaidLink isConnected={false} onConnectedCallback={refreshClients} />
                <button
                  className='btn btn-secondary btn-sm ml-2'
                  type='button'
                  onClick={openPlaidPicker}
                  data-testid='import-cards-from-plaid'
                >
                  {data.t.read('cards_import_from_plaid')}
                </button>
                <button className='btn btn-primary btn-sm ml-2 justify-content-end' type='button' onClick={() => setShowEditModal(true)}>
                  {data.t.read('add')}
                </button>
                <button
                  type='button'
                  className='btn btn-secondary btn-sm ml-2'
                  disabled={selectedCardIds.length === 0}
                  onClick={() => setShowDeleteModal(true)}
                >
                  {data.t.read('delete') + ' (' + selectedCardIds.length + ')'}
                </button>
                <button
                  className='btn btn-secondary btn-sm mx-2'
                  onClick={() => {
                    generateStatements(selectedCardIds);
                  }}
                  disabled={selectedCardIds.length === 0}
                >
                  {data.t.read('cc_statements.re_generate_statements') + ' (' + selectedCardIds.length + ')'}
                </button>
                <DropdownMenu refreshing={refreshing}>
                  <button
                    className='dropdown-item d-flex align-items-center'
                    onClick={() => {
                      refreshData();
                    }}
                    disabled={plaidData.length <= 0}
                  >
                    <FontAwesomeIcon icon={faSync} className='mr-2' />
                    {data.t.read('cc_transactions.refresh_data')}
                  </button>
                  <button onClick={() => setShowImportModal(!showImportModal)} type='button' className='dropdown-item d-flex align-items-center'>
                    {data.t.read('import')}
                  </button>
                </DropdownMenu>
              </div>
            }
          </div>
          <div className='mb-2 card rounded-lg admin-table'>
            <table className='table table-hover font-small'>
              <thead>
                <tr>
                  {
                    <th>
                      <Checkbox disabled={!ccards || !ccards.length} isChecked={isAllSelected()} onChange={selectAllCcards} />
                    </th>
                  }
                  <th className='pointer col-2' onClick={() => sortTable('label')}>
                    {data.t.read('label')}
                    {sorting.find((s) => s.id === 'label') && (
                      <FontAwesomeIcon
                        className={loading ? 'infinite-rotate faded ml-2 fa-xs' : 'ml-2 fa-xs'}
                        icon={sorting.find((s) => s.id === 'label').desc ? faArrowDown : faArrowUp}
                      />
                    )}
                  </th>
                  <th className='pointer col-3' onClick={() => sortTable('card_masked_number')}>
                    {data.t.read('cc_transactions.column_names.account_mask')}
                    {sorting.find((s) => s.id === 'card_masked_number') && (
                      <FontAwesomeIcon
                        className={loading ? 'infinite-rotate faded ml-2 fa-xs' : 'ml-2 fa-xs'}
                        icon={sorting.find((s) => s.id === 'card_masked_number').desc ? faArrowDown : faArrowUp}
                      />
                    )}
                  </th>
                  <th className='pointer col-3' onClick={() => sortTable('card_type')}>
                    {data.t.read('cc_statements.column_names.card_type')}
                    {sorting.find((s) => s.id === 'card_type') && (
                      <FontAwesomeIcon
                        className={loading ? 'infinite-rotate faded ml-2 fa-xs' : 'ml-2 fa-xs'}
                        icon={sorting.find((s) => s.id === 'card_type').desc ? faArrowDown : faArrowUp}
                      />
                    )}
                  </th>
                  <th className='pointer col-2' onClick={() => sortTable('created_at')}>
                    {data.t.read('invoice.created_at')}
                    {sorting.find((s) => s.id === 'created_at') && (
                      <FontAwesomeIcon
                        className={loading ? 'infinite-rotate faded ml-2 fa-xs' : 'ml-2 fa-xs'}
                        icon={sorting.find((s) => s.id === 'created_at').desc ? faArrowDown : faArrowUp}
                      />
                    )}
                  </th>
                  <th className='pointer col-2' onClick={() => sortTable('updated_at')}>
                    {data.t.read('invoice.updated_at')}
                    {sorting.find((s) => s.id === 'updated_at') && (
                      <FontAwesomeIcon
                        className={loading ? 'infinite-rotate faded ml-2 fa-xs' : 'ml-2 fa-xs'}
                        icon={sorting.find((s) => s.id === 'updated_at').desc ? faArrowDown : faArrowUp}
                      />
                    )}
                  </th>
                  {/* TODO: add plaid connection status */}
                  <th className='pointer col-1 text-center' onClick={() => sortTable('active')}>
                    Active
                    {sorting.find((s) => s.id === 'active') && (
                      <FontAwesomeIcon
                        className={loading ? 'infinite-rotate faded ml-2 fa-xs' : 'ml-2 fa-xs'}
                        icon={sorting.find((s) => s.id === 'active').desc ? faArrowDown : faArrowUp}
                      />
                    )}
                  </th>

                  <th className='pointer'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ccards?.map((ccard) => (
                  <tr key={ccard._id?.$oid}>
                    <td>
                      <Checkbox isChecked={selectedCardIds.includes(ccard._id.$oid)} onChange={() => toggleSelect(ccard._id.$oid)} />
                    </td>
                    <td className='col-2 pointer' onClick={() => openEditModal(ccard)}>
                      {ccard.label}
                    </td>
                    <td className='col-3 pointer ml-3' onClick={() => openEditModal(ccard)}>
                      {ccard.card_masked_number}
                    </td>
                    <td className='col-3 pointer ml-3' onClick={() => openEditModal(ccard)}>
                      {data.t.read(`${ccard?.card_type?.toLowerCase()}`)}
                    </td>
                    <td className='col-2 pointer' onClick={() => openEditModal(ccard)}>
                      {ccard.created_at
                        ? Intl.DateTimeFormat(`${data.t.lang}-US`, {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false,
                          }).format(new Date(ccard.created_at?.$date))
                        : ''}
                    </td>
                    <td className='col-2 pointer' onClick={() => openEditModal(ccard)}>
                      {ccard.updated_at
                        ? Intl.DateTimeFormat(`${data.t.lang}-US`, {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false,
                          }).format(new Date(ccard.updated_at?.$date))
                        : ''}
                    </td>
                    <td className='col-1 text-center'>
                      <Switch
                        className='switch mt-0  '
                        checked={ccard?.active ?? true}
                        width={70}
                        onColor='#F39800'
                        onHandleColor='#2693e6'
                        boxShadow='0px 1px 5px rgba(0, 0, 0, 0.6)'
                        activeBoxShadow='0px 0px 1px 10px rgba(0, 0, 0, 0.2)'
                        onChange={(active) => activeCcard({ ...ccard, active: active })}
                      />
                    </td>
                    <td>
                      <div className={'d-flex justify-content-center'}>
                        <FontAwesomeIcon icon={faPencilAlt} onClick={() => openEditModal(ccard)} type='button' />
                        {
                          <FontAwesomeIcon
                            className={'ml-2'}
                            icon={faWindowClose}
                            type='button'
                            style={{ color: 'red' }}
                            onClick={() => {
                              setSelectedCardIds([ccard._id?.$oid]);
                              setShowDeleteModal(true);
                            }}
                          />
                        }
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {showEditModal && (
            <Modal title={`${data.t.read('c-card')}`} cancelAction={() => setShowEditModal(false)}>
              <CcardForm
                data={data}
                client={client}
                ccard={ccard}
                setCcard={setCcard}
                updateCcard={updateCcard}
                createCcard={createCcard}
                setShowEditModal={setShowEditModal}
                refreshCcards={refreshCcards}
              />
            </Modal>
          )}
          {showDeleteModal && (
            <Modal
              title={`${data.t.read('c-card')}}`}
              cancelAction={() => setShowDeleteModal(false)}
              okAction={confirmDelete}
              okText={data.t.read('delete')}
              cancelText={data.t.read('cancel')}
            >
              <p className='text-danger'>{`${data.t.read('delete')} ${data.t.read('c-card')} ?`}</p>
            </Modal>
          )}
          <ImportTransactionsModal
            isOpen={showImportModal}
            data={data}
            onClose={() => {
              setShowImportModal(false);
            }}
            refreshCcards={refreshCcards}
            fileFormatExample={data.t.read('cc_transactions.csv_example')}
          />
          {showPlaidPickerModal && (
            <Modal
              title={data.t.read('cards_import_from_plaid')}
              cancelAction={() => setShowPlaidPickerModal(false)}
              cancelText={data.t.read('cancel')}
              okAction={submitPlaidPicker}
              okText={data.t.read('add')}
              invalid={plaidPickerSubmitting || plaidPickerAccounts.filter((a) => !a.already_imported && plaidPickerSelected.has(a.mask)).length === 0}
              width='720px'
            >
              {plaidPickerLoading ? (
                <i>{data.t.read('please_wait')}...</i>
              ) : plaidPickerAccounts.length === 0 ? (
                <p>{data.t.read('cards_plaid_picker_empty')}</p>
              ) : (
                <div>
                  <div className='d-flex align-items-center mb-2'>
                    <Checkbox
                      isChecked={
                        plaidPickerAccounts.filter((a) => !a.already_imported).length > 0 &&
                        plaidPickerAccounts.filter((a) => !a.already_imported).every((a) => plaidPickerSelected.has(a.mask))
                      }
                      onChange={togglePlaidPickerSelectAll}
                    />
                    <span className='ml-2 font-weight-bold'>{data.t.read('cards_plaid_select_all')}</span>
                  </div>
                  <table className='table table-sm'>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>{data.t.read('card_number')}</th>
                        <th>{data.t.read('card')}</th>
                        <th>{data.t.read('bank')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {plaidPickerAccounts.map((a) => (
                        <tr key={`${a.institution_id || ''}-${a.mask}`}>
                          <td>
                            <Checkbox
                              isChecked={a.already_imported || plaidPickerSelected.has(a.mask)}
                              disabled={a.already_imported}
                              onChange={() => togglePlaidPickerMask(a.mask)}
                            />
                          </td>
                          <td>{a.mask}</td>
                          <td>{a.official_name || a.name || '-'}</td>
                          <td className='text-muted small'>{a.institution_name || a.institution_id || '-'}</td>
                          <td className='text-muted small'>
                            {a.already_imported ? data.t.read('cards_plaid_already_imported') : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Modal>
          )}
        </div>
      )}
    </>
  );
};

export default inject('data')(observer(AdminCcards));
